import { Alert, Tab } from '@mui/material';
import { Fragment, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery } from 'react-query';

import { tradersApi } from 'api';
import { Math } from 'classes';
import { PageHeader, QueryTabPanel, QueryTabs } from 'components';
import { OrdersTab, QueryKey } from 'enums';
import { useMeta } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { formatUtils } from 'utils';

import { TraderActiveOrders } from './ActiveOrders';
import { TraderAllOrders } from './AllOrders';
import { TraderCancelledOrders } from './CancelledOrders';
import { TraderCompletedOrders } from './CompletedOrders';
import { TraderDisputeOrders } from './DisputeOrders';
import { TraderHoldCompletedOrders } from './HoldCompletedOrders';

export const TraderPayoutOrdersPage: React.FC = () => {
  const { t: tOrders } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'features.orders',
  });
  const { t } = useTranslation(TranslationNamespace.Trader, {
    keyPrefix: 'pages.payout_orders.warnings',
  });

  const { data: trader } = useQuery(QueryKey.MyInfo, tradersApi.getMyInfo);
  const { isPayoutVolumeControlEnabled } = useMeta();

  const payoutVolumeAvailable = useMemo(
    () =>
      new Math(trader?.payoutVolumeAvailableBalance ?? 0).plus(
        trader?.payoutVolumeMaxDeviation ?? 0,
      ).value,
    [trader],
  );

  const showVolumeExceeded =
    isPayoutVolumeControlEnabled &&
    !!trader?.payoutVolumeControlEnabled &&
    (trader?.payoutVolumeAvailableBalance ?? 0) <= 0;

  return (
    <Fragment>
      <PageHeader title={tOrders('title')} divider={false} />
      {showVolumeExceeded && (
        <Alert severity="warning" className="tw-mb-4">
          {t('payout_volume_exceeded', {
            amount: formatUtils.formatNumber(payoutVolumeAvailable),
          })}
        </Alert>
      )}
      <QueryTabs tabsEnum={OrdersTab}>
        <Tab value={OrdersTab.Active} label={tOrders('tabs.active')} />
        <Tab value={OrdersTab.Completed} label={tOrders('tabs.completed')} />
        <Tab
          value={OrdersTab.HoldCompleted}
          label={tOrders('tabs.hold_completed')}
        />
        <Tab value={OrdersTab.Cancelled} label={tOrders('tabs.cancelled')} />
        <Tab value={OrdersTab.Dispute} label={tOrders('tabs.dispute')} />
        <Tab value={OrdersTab.All} label={tOrders('tabs.all')} />
      </QueryTabs>
      <QueryTabPanel value={OrdersTab.Active}>
        <TraderActiveOrders />
      </QueryTabPanel>
      <QueryTabPanel value={OrdersTab.Completed}>
        <TraderCompletedOrders />
      </QueryTabPanel>
      <QueryTabPanel value={OrdersTab.HoldCompleted}>
        <TraderHoldCompletedOrders />
      </QueryTabPanel>
      <QueryTabPanel value={OrdersTab.Cancelled}>
        <TraderCancelledOrders />
      </QueryTabPanel>
      <QueryTabPanel value={OrdersTab.Dispute}>
        <TraderDisputeOrders />
      </QueryTabPanel>
      <QueryTabPanel value={OrdersTab.All}>
        <TraderAllOrders />
      </QueryTabPanel>
    </Fragment>
  );
};
